import React, { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Calendar,
  Clock,
  MapPin,
  CheckCircle2,
  XCircle,
  AlertCircle,
  ExternalLink,
  Download,
  Share2,
  RefreshCw,
  User,
  Building2,
  Filter,
} from 'lucide-react';
import { MeetingItem, respondMeeting, getMeetingIcsUrl } from '../../lib/api';

interface MeetingListTabProps {
  meetings: MeetingItem[];
  currentUserId: string;
  onScheduleNew?: () => void;
}

export function MeetingListTab({
  meetings,
  currentUserId,
  onScheduleNew,
}: MeetingListTabProps) {
  const queryClient = useQueryClient();
  const [filter, setFilter] = useState<'all' | 'accepted' | 'pending' | 'cancelled'>('all');

  const respondMutation = useMutation({
    mutationFn: ({ id, action }: { id: string; action: 'accept' | 'decline' | 'cancel' }) =>
      respondMeeting(id, action),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['meetings'] });
    },
    onError: (err: any) => {
      alert(err.message || 'Failed to update meeting status');
    },
  });

  const filteredMeetings = meetings.filter((m) => {
    if (filter === 'all') return true;
    if (filter === 'accepted') return m.status === 'accepted';
    if (filter === 'pending') return m.status === 'pending';
    if (filter === 'cancelled') return m.status === 'declined' || m.status === 'cancelled';
    return true;
  });

  const getGoogleCalendarUrl = (m: MeetingItem) => {
    const parseFormat = (dt: string) => {
      const [d, t] = dt.split(' ');
      const [y, mon, day] = d.split('-');
      const [h, min] = t.split(':');
      // Format YYYYMMDDTHHmmSS
      return `${y}${mon}${day}T${h}${min}00`;
    };
    const startStr = parseFormat(m.start);
    const endStr = parseFormat(m.end);
    const text = encodeURIComponent(m.title);
    const details = encodeURIComponent(
      `${m.agenda || 'B2B Meeting at IIAB Expo 2026'}\n\nParticipants: ${m.requesterName} & ${m.targetName}`
    );
    const location = encodeURIComponent(`${m.location}, Jio World Convention Centre, Mumbai`);
    return `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${text}&dates=${startStr}/${endStr}&details=${details}&location=${location}`;
  };

  return (
    <div className="space-y-6">
      {/* Top Filter Bar */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-white p-4 rounded-2xl border border-slate-200">
        <div className="flex items-center gap-1.5 overflow-x-auto">
          <button
            onClick={() => setFilter('all')}
            className={`px-3 py-1.5 rounded-xl text-xs font-semibold transition-all cursor-pointer ${
              filter === 'all'
                ? 'bg-[#0A3D27] text-white'
                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
            }`}
          >
            All Meetings ({meetings.length})
          </button>
          <button
            onClick={() => setFilter('accepted')}
            className={`px-3 py-1.5 rounded-xl text-xs font-semibold transition-all cursor-pointer ${
              filter === 'accepted'
                ? 'bg-emerald-600 text-white'
                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
            }`}
          >
            Confirmed ({meetings.filter((m) => m.status === 'accepted').length})
          </button>
          <button
            onClick={() => setFilter('pending')}
            className={`px-3 py-1.5 rounded-xl text-xs font-semibold transition-all cursor-pointer ${
              filter === 'pending'
                ? 'bg-amber-500 text-white'
                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
            }`}
          >
            Pending ({meetings.filter((m) => m.status === 'pending').length})
          </button>
        </div>

        {onScheduleNew && (
          <button
            onClick={onScheduleNew}
            className="flex items-center justify-center gap-1.5 px-4 py-2 rounded-xl bg-[#0A3D27] hover:bg-[#062B1B] text-white text-xs font-bold transition-all shadow-xs cursor-pointer"
          >
            <Calendar className="w-3.5 h-3.5" />
            <span>Schedule Meeting</span>
          </button>
        )}
      </div>

      {/* Meetings List */}
      {filteredMeetings.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-3xl border border-dashed border-slate-200 p-8 space-y-3">
          <div className="w-12 h-12 rounded-2xl bg-emerald-50 text-[#0A3D27] flex items-center justify-center mx-auto">
            <Calendar className="w-6 h-6" />
          </div>
          <h3 className="text-sm font-bold text-slate-900">No meetings found</h3>
          <p className="text-xs text-slate-500 max-w-sm mx-auto">
            You don't have any {filter !== 'all' ? filter : ''} meetings scheduled yet. Browse the directory to connect with exhibitors and delegates.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4">
          {filteredMeetings.map((m) => {
            const isIncoming = m.targetId === currentUserId;
            const otherPartyName = isIncoming ? m.requesterName : m.targetName;
            const otherPartyOrg = isIncoming ? m.requesterOrg : m.targetOrg;

            return (
              <div
                key={m.id}
                className="bg-white rounded-2xl border border-slate-200 p-5 space-y-4 hover:shadow-xs transition-shadow"
              >
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-slate-100 pb-3">
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <h4 className="text-sm font-bold text-slate-900">{m.title}</h4>
                      {m.status === 'accepted' && (
                        <span className="px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-emerald-100 text-emerald-800 flex items-center gap-1">
                          <CheckCircle2 className="w-3 h-3" /> Confirmed
                        </span>
                      )}
                      {m.status === 'pending' && (
                        <span className="px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-amber-100 text-amber-800 flex items-center gap-1">
                          <Clock className="w-3 h-3" /> Awaiting Response
                        </span>
                      )}
                      {m.status === 'declined' && (
                        <span className="px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-slate-100 text-slate-600">
                          Declined
                        </span>
                      )}
                      {m.status === 'cancelled' && (
                        <span className="px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-slate-100 text-slate-600">
                          Cancelled
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-slate-500 flex items-center gap-1.5">
                      <User className="w-3.5 h-3.5 text-slate-400" />
                      <span>
                        With <span className="font-semibold text-slate-700">{otherPartyName}</span>{' '}
                        {otherPartyOrg ? `(${otherPartyOrg})` : ''}
                      </span>
                    </p>
                  </div>

                  <div className="flex items-center gap-2">
                    {/* Schedule Timing Badge */}
                    <div className="px-3 py-1.5 bg-slate-50 rounded-xl border border-slate-200 text-xs font-mono text-slate-700 flex items-center gap-1.5">
                      <Calendar className="w-3.5 h-3.5 text-[#0A3D27]" />
                      <span>{m.start}</span>
                    </div>
                  </div>
                </div>

                {/* Location & Agenda */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
                  <div className="flex items-center gap-2 text-slate-600 bg-slate-50/70 p-2.5 rounded-xl border border-slate-100">
                    <MapPin className="w-4 h-4 text-emerald-700 shrink-0" />
                    <span>
                      <strong className="text-slate-800">Location:</strong> {m.location}
                    </span>
                  </div>

                  {m.agenda && (
                    <div className="text-slate-600 bg-slate-50/70 p-2.5 rounded-xl border border-slate-100 italic line-clamp-2">
                      "{m.agenda}"
                    </div>
                  )}
                </div>

                {/* Action Buttons */}
                <div className="flex flex-wrap items-center justify-between gap-3 pt-2">
                  <div className="flex items-center gap-2">
                    {m.status === 'accepted' && (
                      <>
                        <a
                          href={getGoogleCalendarUrl(m)}
                          target="_blank"
                          rel="noreferrer"
                          className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 hover:bg-blue-100 text-blue-700 text-xs font-semibold rounded-xl border border-blue-200 transition-colors"
                        >
                          <ExternalLink className="w-3 h-3" />
                          <span>Google Calendar</span>
                        </a>

                        <a
                          href={getMeetingIcsUrl(m.id)}
                          download
                          className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-50 hover:bg-slate-100 text-slate-700 text-xs font-semibold rounded-xl border border-slate-200 transition-colors"
                        >
                          <Download className="w-3 h-3" />
                          <span>Download .ics</span>
                        </a>
                      </>
                    )}
                  </div>

                  {/* Accept / Decline logic for recipient */}
                  {m.status === 'pending' && isIncoming && (
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => respondMutation.mutate({ id: m.id, action: 'decline' })}
                        disabled={respondMutation.isPending}
                        className="px-3 py-1.5 rounded-xl border border-slate-200 text-xs font-bold text-slate-600 hover:bg-slate-100 transition-colors cursor-pointer"
                      >
                        Decline
                      </button>
                      <button
                        onClick={() => respondMutation.mutate({ id: m.id, action: 'accept' })}
                        disabled={respondMutation.isPending}
                        className="px-4 py-1.5 rounded-xl bg-[#0A3D27] hover:bg-[#062B1B] text-white text-xs font-bold shadow-xs transition-colors cursor-pointer"
                      >
                        Accept Meeting
                      </button>
                    </div>
                  )}

                  {/* Cancel button if already accepted */}
                  {m.status === 'accepted' && (
                    <button
                      onClick={() => {
                        if (confirm('Are you sure you want to cancel this meeting?')) {
                          respondMutation.mutate({ id: m.id, action: 'cancel' });
                        }
                      }}
                      className="text-xs text-rose-600 hover:text-rose-700 font-medium cursor-pointer"
                    >
                      Cancel Meeting
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default MeetingListTab;
